import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Button from '@ui-elements/Button';
import SaveButton from '@ui-elements/SaveButton';
import Checkbox from '@ui-elements/Checkbox';

import BasicModal from '@ui-parts/BasicModal';
import HelpLink from '@ui-parts/HelpLink';

import { Typography } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';

import { SCREEN_VISIBILITY_SETTINGS, SCREEN_VISIBILITY_SETTINGS_IDS } from '~/constants';
import { ROUTES } from '~/constants/routes';
import { FONT_SIZES, FONT_WEIGHTS, PALETTE } from '~/assets/styles/base';

import { createUserScreenVisibilitySettings, callThunk } from '~/api';
import { SURVEY_FORM_TYPE } from '~/common/constants';

const Wrap = styled.div`
  padding: 25px 20px 15px 50px;
`;

const FlexWrap = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`;

const BodyWrapper = styled.div`
  padding: 0 0;
`;

const CheckBoxContainer = styled.div`
  & .MuiFormGroup-root {
    justify-content: center;
  }

  span {
    font-size: ${FONT_SIZES.title3};
  }
`;

const LeftItem = styled.div`
  padding-right: 10px;
  padding-bottom: 10px;
  width: 60%;
`;

const ErrorText = styled.span`
  color: ${PALETTE.error.main};
`;

const StyledTable = styled(Table)`
  padding-bottom: 20px;
`;

const StyledTableBody = styled(TableBody)`
  border: 1px solid ${PALETTE.border.table};
  border-top: none;
  tr {
    background-color: ${PALETTE.white};
    &:first-child {
      border-top: 1px solid ${PALETTE.border.table};
    }
  }
  td {
    font-size: ${FONT_SIZES.body3};
    color: ${PALETTE.text.main};
    height: 44px;
    padding: 8px 10px;
    border-bottom: 1px solid ${PALETTE.border.table};
  }
`;

const NgWrapper = styled.div`
  margin-bottom: 5px;
`;
const NgDetailsWrapper = styled.div`
  margin-bottom: 2px;
`;

function NgChecklistModal({ open, handleClose, handleRegist, dataTestId, formType }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isHidden, setIsHidden] = useState(false);

  let ngChecklist = t('survey.ngCheckList.modal.ngList', { returnObjects: true });

  if (formType === SURVEY_FORM_TYPE.ORIGINAL_FORM) {
    ngChecklist = ngChecklist.slice(0, 4);
  }

  return (
    <BasicModal
      open={open}
      titleProps={{
        customModalTitle: [
          <Wrap key="ngChecklistModalTitleWrap">
            <FlexWrap key="ngChecklistModalTitleFlexWrap">
              <Typography
                variant="title1"
                fontWeight={FONT_WEIGHTS.size1}
                data-testid="uiParts-NgChecklistModalHeader-title"
              >
                {t('survey.ngCheckList.modal.title')}
              </Typography>
              {formType === SURVEY_FORM_TYPE.GOOGLE_FORM && (
                <HelpLink
                  data-testid="uiParts-NgChecklistModalHeader-helpLink"
                  warnText={t('survey.ngCheckList.modal.confirmDetails')}
                  warnTop="20px"
                  warnRight="50px"
                  warnLink={ROUTES.COMMON.ASK_NG_CHECKLIST}
                />
              )}
            </FlexWrap>
            <FlexWrap key="ngChecklistModalTitleFlexWrap2">
              <BlockIcon sx={{ color: PALETTE.error.main, fontSize: 21, marginRight: 1 }} />
              <Typography
                variant="title2"
                component="p"
                fontWeight={FONT_WEIGHTS.base}
                data-testid="uiParts-NgChecklistModalHeader-desc"
              >
                <ErrorText>{t('appCommon.ngCheckList')}</ErrorText>
                {t('survey.ngCheckList.modal.desc')}
              </Typography>
            </FlexWrap>
          </Wrap>,
        ],
      }}
      handleClose={handleClose}
      buttons={[
        <Button
          key="button1"
          sx={{ width: 148, marginTop: 2 }}
          dataTestId="ngChecklistModal-backBtn"
          variant="outlined"
          color="secondary"
          colorType="gray"
          handleClick={() => {
            handleClose();
          }}
        >
          {t('appCommon.back')}
        </Button>,
        <SaveButton
          key="button2"
          sx={{ width: 148, marginTop: 2 }}
          dataTestId="ngChecklistModal-saveBtn"
          handleClick={async () => {
            if (isHidden) {
              await dispatch(
                callThunk({
                  action: createUserScreenVisibilitySettings,
                  payload: {
                    screenVisibilitySettingId:
                      SCREEN_VISIBILITY_SETTINGS_IDS[
                        SCREEN_VISIBILITY_SETTINGS.SURVEY_REGISTER_NG_CHECKLIST_HIDDEN
                      ],
                  },
                })
              );
            }
            handleRegist();
          }}
          label={t('appCommon.setting')}
        />,
      ]}
      aboveButtons={
        <CheckBoxContainer>
          <Checkbox
            name="ngChecklistModal-HiddenCheckbox"
            label={t('survey.ngCheckList.modal.hiddenCheckbox')}
            checked={isHidden}
            handleChange={() => {
              setIsHidden(!isHidden);
            }}
            dataTestId="ngChecklistModal-HiddenCheckbox"
          />
        </CheckBoxContainer>
      }
      dataTestId={dataTestId ? `${dataTestId}` : `NgChecklistModal`}
      width="880px"
      scrollable
    >
      <BodyWrapper>
        <LeftItem>
          <Typography variant="title2" fontWeight="bold" data-testid="ngChecklistModal-tableTitle">
            {t('survey.ngCheckList.modal.label')}
          </Typography>
        </LeftItem>
        <TableContainer>
          <StyledTable data-testid="ngChecklistModal-table">
            <StyledTableBody>
              {ngChecklist.map((ng, i) => {
                return (
                  <TableRow key={ng.title} data-testid="ngChecklistModal-tableRow">
                    <TableCell component="td" scope="row">
                      <BlockIcon sx={{ color: PALETTE.error.main, fontSize: 29, marginRight: 1 }} />
                    </TableCell>
                    <TableCell component="td" scope="row">
                      <NgWrapper>
                        <Typography
                          variant="title2"
                          fontWeight="bold"
                          data-testid={`ngChecklistModal-tableCell-${i}-title`}
                        >
                          {ng.title}
                        </Typography>
                      </NgWrapper>
                      {Array.isArray(ng.details) &&
                        ng.details.length > 0 &&
                        ng.details.map((detail) => {
                          return (
                            <NgDetailsWrapper key={detail}>
                              <Typography
                                variant="title3"
                                component="p"
                                fontWeight="medium"
                                data-testid="uiParts-CustomModalDesc"
                                key={detail}
                              >
                                ・{detail}
                              </Typography>
                            </NgDetailsWrapper>
                          );
                        })}
                    </TableCell>
                  </TableRow>
                );
              })}
            </StyledTableBody>
          </StyledTable>
        </TableContainer>
      </BodyWrapper>
    </BasicModal>
  );
}

NgChecklistModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleRegist: PropTypes.func,
  dataTestId: PropTypes.string,
  formType: PropTypes.number,
};

export default NgChecklistModal;
